import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Megaphone, LogOut, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { staffLogout, staffMe } from "@/lib/api";
import {
  STAFF_LOGIN_URL,
  MARKETING_WORKSPACE_URL,
} from "@/lib/constants";

/**
 * Staff Portal landing page — cookie-gated tile board. Shown after a
 * successful /staff/login. Renders nothing sensitive while the auth
 * check is in flight.
 */
export default function StaffPortal() {
  const [state, setState] = useState("checking"); // checking | authed | unauthed
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    staffMe()
      .then(() => {
        if (!cancelled) setState("authed");
      })
      .catch(() => {
        if (!cancelled) setState("unauthed");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "checking") {
    return (
      <main
        data-testid="staff-portal-loading"
        className="min-h-[70vh] flex items-center justify-center text-[#6B7280]"
      >
        Checking session…
      </main>
    );
  }
  if (state === "unauthed") return <Navigate to="/staff/login" replace />;

  const onLogout = async () => {
    try {
      await staffLogout();
    } catch (_e) {
      // still redirect — cookie may have already been cleared
    }
    toast.success("Signed out.");
    nav("/staff/login", { replace: true });
  };

  const tiles = [
    {
      href: STAFF_LOGIN_URL,
      title: "Pabau Portal",
      hint: "Schedules · client records · notes",
      body:
        "Your day-to-day clinical workspace. Check your schedule, look up client history, add treatment notes, and manage bookings — the same Pabau workspace you use inside the clinic, now one click away from home.",
      icon: Calendar,
      testid: "staff-tile-pabau",
    },
    {
      href: MARKETING_WORKSPACE_URL,
      title: "Marketing Workspace",
      hint: "Review · approve · decline social posts",
      body:
        "Marketing Workspace — where you review drafts before they go live on Facebook, Instagram, and LinkedIn. You'll land on your personal review page and, for each post, you can approve, decline (with a note back to the drafter), or open a discussion thread. AI-drafted posts are tagged so you always know what's what.",
      icon: Megaphone,
      testid: "staff-tile-marketing",
    },
  ];

  return (
    <main
      data-testid="staff-portal-page"
      className="bg-[#F5F5F5] min-h-[80vh]"
    >
      <SEO
        title="Staff Portal — Ashlan Clinic"
        description="Internal staff tools for Ashlan Clinic."
        path="/staff"
        noIndex
      />
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="flex items-start justify-between gap-6">
          <div>
            <span className="ash-eyebrow !text-[#3A7A8C]">Staff Portal</span>
            <h1 className="mt-3 text-4xl md:text-5xl font-bold tracking-tight text-[#080808]">
              Welcome back.
            </h1>
            <p className="mt-4 text-[#3C4048] leading-relaxed max-w-2xl">
              This is your staff home — the quickest way into the two systems
              that keep our days running. <strong>Pabau</strong> handles the
              clinical side: schedules, client records, treatment notes, and
              bookings. <strong>Marketing Workspace</strong> is where our
              social posts (Facebook, Instagram, LinkedIn) are reviewed before
              they go live. Tap a card to open it in a new tab, so this page
              stays bookmarked as your starting point.
            </p>
            <p className="mt-3 text-sm text-[#6B7280] max-w-2xl leading-relaxed">
              You&apos;ll stay signed in for 30 days on this device — use{" "}
              <em>Sign out</em> at the top right if you&apos;re on a shared
              computer.
            </p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            data-testid="staff-logout-button"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold text-[#6B7280] hover:text-[#080808] transition-colors"
          >
            <LogOut size={14} strokeWidth={2} />
            Sign out
          </button>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {tiles.map((t) => (
            <a
              key={t.testid}
              href={t.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={t.testid}
              className="bg-white p-8 md:p-10 border-l-2 border-[#5FA8B4] hover:shadow-lg transition-shadow group flex flex-col"
            >
              <div className="flex items-center justify-between">
                <t.icon size={32} strokeWidth={1.5} className="text-[#3A7A8C]" />
                <ArrowUpRight
                  size={20}
                  strokeWidth={1.75}
                  className="text-[#6B7280] group-hover:text-[#3A7A8C] transition-colors"
                />
              </div>
              <h2 className="mt-8 text-2xl md:text-3xl font-bold tracking-tight text-[#080808]">
                {t.title}
              </h2>
              <p className="mt-2 text-xs uppercase tracking-[0.16em] font-semibold text-[#3A7A8C]">
                {t.hint}
              </p>
              <p className="mt-4 text-[#3C4048] leading-relaxed text-[15px]">
                {t.body}
              </p>
            </a>
          ))}
        </div>
      </section>
    </main>
  );
}
